import { db } from '@app/icare/client/db'
import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { localResultOneQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { sync } from '@app/icare/client/sync'
import { patientRelations } from '@app/icare/db/query/patientRelations'
import type { PatientFormIdType } from '@app/icare/db/schema/form/PatientFormCommon'
import { PatientFormId } from '@app/icare/db/schema/form/PatientFormCommon'
import { type PatientSupplyForm, PatientSupplyFormSchema } from '@app/icare/db/schema/form/PatientSupplyForm'
import type { PatientIdType } from '@app/icare/entities/patient/schema/Patient'
import { PatientCareServiceId } from '@app/icare/entities/patient/schema/PatientCareService'
import { PatientClinicId } from '@app/icare/entities/patient/schema/PatientClinic'
import { PatientCostCarrierId } from '@app/icare/entities/patient/schema/PatientCostCarrier'
import { PatientDoctorId } from '@app/icare/entities/patient/schema/PatientDoctor'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { PatientMedicalTechnologyId } from '@app/icare/entities/patient/schema/PatientMedicalTechnology'
import { PatientPharmacyId } from '@app/icare/entities/patient/schema/PatientPharmacy'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { useAppForm } from '@app/icare/form/useAppForm'
import { t } from '@lingui/core/macro'
import { zodParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ConfigProvider, Form } from 'antd'
import { z } from 'zod'
import { EditPatientSupply } from './-edit'
import { NewPatientSupply } from './-new'

const patientRelationsQuery = (patientId: PatientIdType) =>
  localResultOneQueryOptions({
    query: db
      .selectFrom('patient')
      .selectAll()
      .select([
        patientRelations.costCarriers({ activeOnly: true }),
        patientRelations.doctors({ activeOnly: true }),
        patientRelations.clinics({ activeOnly: true }),
        patientRelations.careServices({ activeOnly: true }),
        patientRelations.medicalTechnologies({ activeOnly: true }),
        patientRelations.pharmacies({ activeOnly: true }),
      ])
      .where('id', '=', patientId),
  })

export const Route = createFileRoute(
  '/_app/patienten/$id/_edit/_documents/_forms/_unterschriftdokumente/versorgungsauftrag/$form/',
)({
  component: RouteComponent,
  params: zodParams(z.object({ id: PatientId.ref, form: z.string() })),
  staticData: {
    layout: {
      menuRender: false,
    },
  },
  loader: async ({ params, context: { queryClient } }) => {
    await queryClient.ensureQueryData(patientRelationsQuery(params.id))

    if (params.form === 'new') return
  },
})

function RouteComponent() {
  const { id, form: formId } = Route.useParams() as { id: PatientIdType; form: PatientFormIdType | 'new' }
  const router = useRouter()

  const { data: patient } = useSuspenseQuery(patientRelationsQuery(id))

  const isNew = formId === 'new'

  const current = useQuery(localQueryEntityOptions(formId))

  const form = useAppForm(
    appFormOptions(PatientSupplyFormSchema, {
      defaultValues: (current.data as PatientSupplyForm) ?? {
        patientId: id,
        type: 'PatientSupply',
        status: 'new',
        id: PatientFormId.generate(),
        form: {
          costCarrier: {
            privateInsured: patient?.costCarrier?.privateInsured,
          },
        },
      },
      toast: { successTitle: t`Versorgungsauftrag gespeichert` },
      onSubmit: async ({ value }) => {
        const missingRelationMutations: Promise<unknown>[] = []

        const selectedCostCarrier = value.form.costCarrier?.value
        if (
          selectedCostCarrier &&
          !(patient?.costCarriers ?? []).some(relation => relation.toId === selectedCostCarrier)
        ) {
          missingRelationMutations.push(
            sync.mutate({
              id: PatientCostCarrierId.generate(),
              patientId: id,
              toId: selectedCostCarrier,
            }),
          )
        }

        const selectedDoctor = value.form.doctor?.value
        if (selectedDoctor && !(patient?.doctors ?? []).some(relation => relation.toId === selectedDoctor)) {
          missingRelationMutations.push(
            sync.mutate({
              id: PatientDoctorId.generate(),
              patientId: id,
              toId: selectedDoctor,
              primaryDoctor: {
                active: false,
              },
            }),
          )
        }

        const selectedClinic = value.form.clinic?.value
        if (selectedClinic && !(patient?.clinics ?? []).some(relation => relation.toId === selectedClinic)) {
          missingRelationMutations.push(
            sync.mutate({
              id: PatientClinicId.generate(),
              patientId: id,
              toId: selectedClinic,
            }),
          )
        }

        const selectedCareService = value.form.careService?.value
        if (
          selectedCareService &&
          !(patient?.careServices ?? []).some(relation => relation.toId === selectedCareService)
        ) {
          missingRelationMutations.push(
            sync.mutate({
              id: PatientCareServiceId.generate(),
              patientId: id,
              toId: selectedCareService,
            }),
          )
        }

        const selectedMedicalTechnology = value.form.medicalTechnology?.value
        if (
          selectedMedicalTechnology &&
          !(patient?.medicalTechnologies ?? []).some(relation => relation.toId === selectedMedicalTechnology)
        ) {
          missingRelationMutations.push(
            sync.mutate({
              id: PatientMedicalTechnologyId.generate(),
              patientId: id,
              toId: selectedMedicalTechnology,
            }),
          )
        }

        const selectedPharmacy = value.form.pharmacy?.value
        if (selectedPharmacy && !(patient?.pharmacies ?? []).some(relation => relation.toId === selectedPharmacy)) {
          missingRelationMutations.push(
            sync.mutate({
              id: PatientPharmacyId.generate(),
              patientId: id,
              toId: selectedPharmacy,
            }),
          )
        }

        await sync.mutate(value)
        if (missingRelationMutations.length > 0) {
          await Promise.all(missingRelationMutations)
        }

        if (isNew) {
          await router.navigate({
            to: '/patienten/$id/unterschriftsdokumente/versorgungsauftrag',
            replace: true,
            params: { id },
          })
        }
      },
    }),
  )

  return (
    <form.AppForm>
      <form.ProForm editablePolicy={{ forceMode: isNew ? 'edit' : 'read' }}>
        <ConfigProvider
          theme={{
            components: {
              Form: {
                itemMarginBottom: isNew ? 10 : 0,
              },
            },
          }}
        >
          <Form component={false} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
            {isNew ? <NewPatientSupply /> : <EditPatientSupply />}
          </Form>
        </ConfigProvider>
      </form.ProForm>
    </form.AppForm>
  )
}
